import namensnennung from "@design/icons/namensnennung.txt?raw";
import { berlinUhrzeit } from "@dj/domain/gemeinsam";
import { Fragment, useEffect, useState } from "react";
import { Ansicht } from "../design/Ansicht";
import { daemmerungAb, darstellungAnwenden, DARSTELLUNG_SCHLUESSEL, gewaehlteDarstellung, ortDerAutomatik, regelAusTokens } from "../design/darstellung";
import { Auswahl, Schalter } from "../design/Formular";
import { Abweisung } from "../design/Hinweis";
import { Kachel } from "../design/Kachel";
import { vibrieren } from "../plattform/geraet";
import { IphoneHinweis } from "../plattform/IphoneHinweis";
import { useSitzung } from "../plattform/sitzung";
import { abonniert, benachrichtigungenAusschalten, benachrichtigungenEinschalten } from "./abonnement";

/**
 * Einstellungen (oberflaeche.md §7): Darstellung (Automatisch, Tag, Dämmerung; setzt `data-modus`), Vibration prüfen,
 * Benachrichtigungen, Hinweis für das iPhone, „Info“ mit der Namensnennung der Symbole und der Schrift
 * (design/README §8.4, Wortlaut aus `design/icons/namensnennung.txt`; CC BY verlangt sie sichtbar; als Kleingedrucktes nach
 * Anforderungen K107). „Automatisch“ schaltet
 * nach dem Sonnenstand am Ort der Drückjagd (design/darstellung.ts); die Erklärung nennt den heutigen Beginn der
 * Dämmerung (Referenz `#s-einstellungen`).
 */
export { darstellungAnwenden, DARSTELLUNG_SCHLUESSEL };

export function Einstellungen() {
  const { uid } = useSitzung();
  const [darstellung, setDarstellung] = useState<string>(gewaehlteDarstellung);
  const [benachrichtigungen, setBenachrichtigungen] = useState(() => !!uid && abonniert(uid));
  const [grund, setGrund] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem(DARSTELLUNG_SCHLUESSEL, darstellung);
    darstellungAnwenden(darstellung);
  }, [darstellung]);

  async function schalten(an: boolean) {
    if (!uid) return;
    setGrund(null);
    if (an) {
      const g = await benachrichtigungenEinschalten(uid).catch(() => "abgelehnt");
      setBenachrichtigungen(g === null);
      if (g && g !== "abgelehnt") setGrund(g);
    } else {
      await benachrichtigungenAusschalten(uid);
      setBenachrichtigungen(false);
    }
  }

  return (
    <Ansicht name="Einstellungen" symbol="einstellungen">
      <div className="text-bereich platte">
        <Auswahl label="Darstellung" optionen={["Automatisch", "Tag", "Dämmerung"]} wert={darstellung} onChange={setDarstellung}
          symbole={{ Automatisch: "automatisch", Tag: "tag", "Dämmerung": "daemmerung" }} />
        {darstellung === "Automatisch" ? <AutomatikErklaerung /> : null}
      </div>
      <div className="text-bereich platte">
        <div className="zeile__t">Signale am Stand</div>
        <p className="erkl">Telefon auf lautlos mit Vibration stellen: Auf vielen Android-Telefonen vibriert es dann bei Mitteilungen. Auf dem iPhone vibriert die App nicht; dort zeigt sie den Wechsel nur an.</p>
        <Kachel symbol="vibration" begriff="Vibration" art="quer" onPress={() => vibrieren("jagd-im-gange")} />
      </div>
      <div className="text-bereich platte">
        <Schalter label="Benachrichtigungen" an={benachrichtigungen} onChange={(an) => void schalten(an)} />
        <Abweisung grund={grund} />
        <IphoneHinweis />
      </div>
      <section className="text-bereich platte" role="region" aria-label="Info">
        <h2 className="kopf">Info</h2>
        <p className="erkl namensnennung">{mitUmbruchstellen(namensnennung.trim())}</p>
      </section>
    </Ansicht>
  );
}

/** „Automatisch“ erklärt: nach Sonnenstand, heutiger Beginn der Dämmerung am gemerkten Ort, nie während einer Meldung. */
function AutomatikErklaerung() {
  const ab = daemmerungAb(new Date(), ortDerAutomatik(), regelAusTokens());
  return (
    <p className="erkl">
      Automatisch nach Sonnenstand am Revier.{ab ? ` Heute Dämmerung ab ${berlinUhrzeit(ab)}.` : null} Wechselt nie während einer Meldung.
    </p>
  );
}

/** Adressen in der Namensnennung dürfen nach jedem „/“ umbrechen (schmale Bildschirme, große Schrift). */
function mitUmbruchstellen(text: string) {
  return text.split("/").map((teil, i) => <Fragment key={i}>{i ? <>/<wbr /></> : null}{teil}</Fragment>);
}
